// Коллекция (UX-вайрфрейм, экран 06): фоны / инструменты / противники,
// открытые прогрессом. Открытое — будущая палитра мастерской (M4).
// Вкладка «История» — сюжет-ролики (docs/story-videos.md): открываются по мере
// прохождения, пересматриваются в модале; в общий счётчик деталей не входят.
//
// Состояние (unlock/hidden/lockHint/videoUrl/world-тайтлы) считает бэк —
// GET /me/collection (§4/§5 спеки миграции). Экран только рендерит ответ:
// своей логики открытия/прогресса здесь больше нет.

import { useEffect, useState } from 'react';
import { loadCollection, type CollectionItem, type CollectionResponse, type StoryItem } from '@/api/collection';
import { LOCKED_ART } from '@/meta/lockedArt';
import { BottomNav } from '@/meta/Shell';
import { usePrefs } from '@/meta/prefsStore';
import { StoryModal } from '@/meta/StoryPlayer';
import { markStoryWatched, useStoryWatched } from '@/save/storyStore';

type CollectionKind = CollectionItem['kind'];
type TabKind = CollectionKind | 'story';

const TABS: { kind: TabKind; label: string }[] = [
  { kind: 'bg', label: 'Фоны' },
  { kind: 'tool', label: 'Инструменты' },
  { kind: 'enemy', label: 'Противники' },
  { kind: 'story', label: 'История' },
];

const THUMB_BG: Record<CollectionKind, string> = {
  bg: 'var(--acc)',
  tool: 'var(--acc-2)',
  enemy: 'var(--danger)',
};

export function CollectionScreen() {
  const [data, setData] = useState<CollectionResponse | null>(null);
  const [tab, setTab] = useState<TabKind>('bg');
  // модал пересмотра ролика
  const [playing, setPlaying] = useState<StoryItem | null>(null);
  const { isWatched } = useStoryWatched();
  const prefs = usePrefs();

  useEffect(() => {
    let cancelled = false;
    loadCollection().then((d) => { if (!cancelled) setData(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const openEpisode = (ep: StoryItem) => {
    markStoryWatched(ep.id); // пересмотр = просмотр: бейдж «новое» гаснет
    setPlaying(ep);
  };

  // Заголовки секций-миров — из ответа (data.worlds, title скрыт у не-достигнутого
  // мира). Секция рендерится только если у мира есть карточки текущего типа / эпизоды.
  const worlds = data ? [...data.worlds].sort((a, b) => a.index - b.index) : [];

  return (
    <main className="meta-screen" data-testid="screen-collection">
      <header className="meta-subbar">
        <h1>Коллекция</h1>
        {data && (
          <span className="counter" data-testid="collection-counter">
            открыто {data.counter.unlocked} / {data.counter.total}
          </span>
        )}
      </header>
      <div className="meta-body">
        <div className="tab-row" role="tablist" aria-label="Разделы коллекции">
          {TABS.map((t) => (
            <button
              key={t.kind}
              role="tab"
              className="tab-btn"
              aria-selected={tab === t.kind}
              data-testid={`tab-${t.kind}`}
              onClick={() => setTab(t.kind)}
            >
              {t.label}
            </button>
          ))}
        </div>
        {!data && <p>Загрузка…</p>}
        {data && tab === 'story' && worlds.map((w) => {
          const eps = data.story.filter((i) => i.world === w.id);
          if (eps.length === 0) return null;
          const open = eps.filter((i) => i.unlocked).length;
          return (
            <section key={w.id} className="collect-world" data-testid={`story-world-${w.id}`}>
              <div className="collect-world-head">
                <h2>Мир {w.index} · {w.title ?? '???'}</h2>
                <span className="counter">{open}/{eps.length}</span>
              </div>
              <div className="card-grid story-grid">
                {eps.map((ep) => ep.unlocked ? (
                  <button
                    key={ep.id}
                    type="button"
                    className="collect-card story-card"
                    data-testid={`story-${ep.id}`}
                    data-unlocked="true"
                    onClick={() => openEpisode(ep)}
                  >
                    <span className="thumb" data-kind="story" aria-hidden="true">
                      <span className="story-play">▶</span>
                    </span>
                    {!isWatched(ep.id) && <span className="story-new">новое</span>}
                    <b>{ep.title}</b>
                    <small>{ep.note}</small>
                  </button>
                ) : (
                  // названия закрытых эпизодов не палим — интрига сюжета
                  <div
                    key={ep.id}
                    className="collect-card story-card locked"
                    data-testid={`story-${ep.id}`}
                  >
                    <span className="thumb" data-kind="story" aria-hidden="true">
                      <img src={LOCKED_ART} alt="" />
                    </span>
                    <b>???</b>
                    <small>{ep.lockHint}</small>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
        {data && tab !== 'story' && worlds.map((w) => {
          const cards = data.items.filter((i) => i.kind === tab && i.world === w.id);
          if (cards.length === 0) return null;
          const open = cards.filter((i) => i.unlocked).length;
          return (
            <section key={w.id} className="collect-world" data-testid={`collect-world-${w.id}`}>
              <div className="collect-world-head">
                <h2>Мир {w.index} · {w.title ?? '???'}</h2>
                <span className="counter">{open}/{cards.length}</span>
              </div>
              <div className="card-grid">
                {cards.map((i) => (
                  <div
                    key={i.id}
                    className={i.unlocked ? 'collect-card' : 'collect-card locked'}
                    data-testid={`collect-${i.id}`}
                    data-unlocked={i.unlocked || undefined}
                  >
                    <span
                      className="thumb"
                      data-kind={i.kind}
                      style={i.unlocked ? { background: THUMB_BG[i.kind] } : undefined}
                      aria-hidden="true"
                    >
                      {i.unlocked
                        ? (i.art ? <img src={i.art} alt="" /> : i.glyph)
                        : <img src={LOCKED_ART} alt="" />}
                    </span>
                    <b>{i.unlocked || !i.hidden ? i.title : '???'}</b>
                    <small>{i.unlocked ? i.note : i.lockHint}</small>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
        <p className="hint" style={{ marginTop: 16 }}>
          {tab === 'story'
            ? 'Ролики открываются по мере прохождения — открытые можно пересматривать.'
            : 'Открытые детали станут палитрой мастерской.'}
        </p>
      </div>
      {playing && (
        <StoryModal
          title={playing.title ?? '???'}
          url={playing.videoUrl}
          volume={prefs.music}
          onClose={() => setPlaying(null)}
        />
      )}
      <BottomNav />
    </main>
  );
}
